import React, { useCallback } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core'
import AssignmentIcon from '@material-ui/icons/Assignment'
import HeadsetIcon from '@material-ui/icons/Headset'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import MenuBookIcon from '@material-ui/icons/MenuBook'
import MicIcon from '@material-ui/icons/Mic'
import PhoneIcon from '@material-ui/icons/Phone'
import { SvgIconComponent } from '@material-ui/icons'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import clsx from 'clsx'

import { useVoiceAgent } from '../../hooks/useVoiceAgent'
import { useVoiceAgentQuestions } from '../../hooks/useVoiceAgentQuestions'
import { useVoiceAgentStyles } from './useVoiceAgentStyles'
import { usePhoneStyles } from '../Phone/usePhoneStyles'
import { Routes } from '../../navigation/config/routes'
import { VoiceAgentStatus } from '../../models/VoiceAgent'
import { formatE164 } from './constants'
import { PauseResumeControl } from './components/PauseResumeControl'
import { CallLogPanel } from './components/CallLogPanel'
import { AgentPersonaPanel } from './components/AgentPersonaPanel'
import { KnowledgePanel } from './components/KnowledgePanel'
import { CallQuestionsPanel } from './components/CallQuestionsPanel'
import { LiveCalls } from './components/LiveCalls'
import { IntakePanel } from './components/IntakePanel'

type VoiceAgentTab = 'calls' | 'agent' | 'knowledge' | 'questions' | 'intake'

const TABS: { id: VoiceAgentTab; label: string; icon: SvgIconComponent }[] = [
    { id: 'calls', label: 'Call Log & Transcripts', icon: HeadsetIcon },
    { id: 'agent', label: 'Agent Voice & Persona', icon: MicIcon },
    { id: 'knowledge', label: 'Knowledge Base', icon: MenuBookIcon },
    { id: 'questions', label: 'Call Questions', icon: HelpOutlineIcon },
    { id: 'intake', label: 'Caller Intake', icon: AssignmentIcon },
]

const isTab = (value: string | null): value is VoiceAgentTab =>
    value === 'calls' ||
    value === 'agent' ||
    value === 'knowledge' ||
    value === 'questions' ||
    value === 'intake'

const statusLabel = (status: VoiceAgentStatus): string => {
    if (status === 'paused') return 'Paused'
    if (status === 'pending') return 'Active'
    if (status === 'live') return 'Live'
    return 'Draft'
}

export const VoiceAgentHome: React.FC = () => {
    const classes = useVoiceAgentStyles()
    const phone = usePhoneStyles()
    const history = useHistory()
    const location = useLocation()
    const {
        setupData,
        isLoading,
        error,
        refetch,
        isApartment,
        pause,
        resume,
    } = useVoiceAgent()
    const { pendingCount } = useVoiceAgentQuestions('pending')

    const params = new URLSearchParams(location.search)
    const tab: VoiceAgentTab = isTab(params.get('tab'))
        ? (params.get('tab') as VoiceAgentTab)
        : 'calls'
    const selectedCallId = params.get('call') || undefined

    const setTab = (_event: React.ChangeEvent<{}>, next: VoiceAgentTab) => {
        const nextParams = new URLSearchParams(location.search)
        nextParams.set('tab', next)
        if (next !== 'calls') nextParams.delete('call')
        history.replace({
            pathname: Routes.manage.voiceAgent.root,
            search: `?${nextParams.toString()}`,
        })
    }

    const onSelectCall = useCallback(
        (id: string) => {
            const nextParams = new URLSearchParams(location.search)
            nextParams.set('tab', 'calls')
            nextParams.set('call', id)
            history.replace({
                pathname: Routes.manage.voiceAgent.root,
                search: `?${nextParams.toString()}`,
            })
        },
        [history, location.search],
    )

    const onDeselectCall = useCallback(() => {
        const nextParams = new URLSearchParams(location.search)
        nextParams.delete('call')
        history.replace({
            pathname: Routes.manage.voiceAgent.root,
            search: `?${nextParams.toString()}`,
        })
    }, [history, location.search])

    if (!isApartment) {
        return (
            <Box className={classes.emptyRoot}>
                <Box className={clsx(classes.card, classes.emptyCard)}>
                    <Typography variant="h6">
                        Voice Agent is available for apartment properties only.
                    </Typography>
                </Box>
            </Box>
        )
    }

    if (isLoading) {
        return (
            <Box className={classes.emptyRoot}>
                <CircularProgress />
            </Box>
        )
    }

    const status = setupData?.status
    const isNotFound = error?.response?.status === 404
    const hasLoadError = !!error && !isNotFound && !setupData

    if (hasLoadError) {
        return (
            <Box className={classes.emptyRoot}>
                <Box className={clsx(classes.card, classes.emptyCard)}>
                    <Typography variant="h6" gutterBottom>
                        Unable to load Voice Agent
                    </Typography>
                    <Typography
                        color="textSecondary"
                        style={{ marginBottom: 16 }}
                    >
                        Something went wrong. Please try again.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.btnPrimary}
                        onClick={() => refetch()}
                    >
                        Retry
                    </Button>
                </Box>
            </Box>
        )
    }

    if (!setupData || status === 'draft' || isNotFound) {
        return (
            <Box className={classes.emptyRoot}>
                <Box className={clsx(classes.card, classes.emptyCard)}>
                    <Box className={classes.emptyIconWrap}>
                        <PhoneIcon />
                    </Box>
                    <Typography className={classes.title}>
                        Answer leasing calls with AI
                    </Typography>
                    <Typography
                        color="textSecondary"
                        style={{ marginTop: 8, marginBottom: 24 }}
                    >
                        Forward your office line to a dedicated number. The
                        agent answers after hours and when your team is busy.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.btnPrimaryLg}
                        onClick={() =>
                            history.push(Routes.manage.voiceAgent.setup)
                        }
                    >
                        Set up Voice Agent
                    </Button>
                </Box>
            </Box>
        )
    }

    if (status !== 'live' && status !== 'paused' && status !== 'pending') {
        return <Redirect to={Routes.manage.voiceAgent.setup} />
    }

    const isLive = status === 'live' || status === 'pending'
    const toggleLoading = pause.isLoading || resume.isLoading

    return (
        <Box className={phone.root}>
            <Box className={phone.pageHeader}>
                <Box className={phone.headerLeft}>
                    <Box className={phone.titleRow}>
                        <Box className={phone.titleIcon}>
                            <PhoneIcon fontSize="small" />
                        </Box>
                        <Box>
                            <Typography variant="h6">Voice Agent</Typography>
                            <Typography className={phone.pageSubtitle}>
                                Incoming calls are answered by AI, 24/7.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Box className={phone.headerRight}>
                    <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                        <Chip
                            size="small"
                            className={
                                isLive ? phone.liveChip : classes.pausedChip
                            }
                            label={
                                <Box display="flex" alignItems="center">
                                    <span
                                        className={clsx(
                                            phone.liveDot,
                                            !isLive && classes.pausedDot,
                                        )}
                                    />
                                    {statusLabel(status)}
                                </Box>
                            }
                        />
                        <PauseResumeControl
                            isLive={isLive}
                            isLoading={toggleLoading}
                            onPause={() => pause.mutate()}
                            onResume={() => resume.mutate()}
                        />
                    </Box>
                    {setupData.number && (
                        <Typography className={phone.phoneNumber}>
                            {formatE164(setupData.number.e164)}
                        </Typography>
                    )}
                    {setupData.human_transfer_e164 && (
                        <Typography
                            color="textSecondary"
                            style={{ fontSize: 12, marginTop: 2 }}
                        >
                            Transfers to{' '}
                            {formatE164(setupData.human_transfer_e164)}
                        </Typography>
                    )}
                </Box>
            </Box>

            <LiveCalls />

            <Box className={phone.tabsWrapper}>
                <Tabs
                    value={tab}
                    onChange={setTab}
                    variant="fullWidth"
                    className={phone.tabs}
                    TabIndicatorProps={{ style: { display: 'none' } }}
                >
                    {TABS.map((item) => {
                        const Icon = item.icon
                        return (
                            <Tab
                                key={item.id}
                                className={phone.tab}
                                value={item.id}
                                label={
                                    <Box className={phone.tabLabel}>
                                        <Icon className={phone.tabIcon} />
                                        <span>{item.label}</span>
                                        {item.id === 'questions' &&
                                            pendingCount > 0 && (
                                                <span
                                                    className={
                                                        classes.questionBadge
                                                    }
                                                >
                                                    {pendingCount}
                                                </span>
                                            )}
                                    </Box>
                                }
                            />
                        )
                    })}
                </Tabs>
            </Box>

            <Box className={phone.content}>
                {tab === 'calls' && (
                    <CallLogPanel
                        selectedCallId={selectedCallId}
                        onSelectCall={onSelectCall}
                        onDeselectCall={onDeselectCall}
                    />
                )}
                {tab === 'agent' && <AgentPersonaPanel />}
                {tab === 'knowledge' && <KnowledgePanel />}
                {tab === 'questions' && <CallQuestionsPanel />}
                {tab === 'intake' && <IntakePanel />}
            </Box>
        </Box>
    )
}
